import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  makeStyles,
  Paper,
  TextField,
  Typography,
  Divider,
  InputAdornment,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from "@material-ui/core";
import { Info, Edit } from "@material-ui/icons";

import { green } from "@material-ui/core/colors";
import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import axios from "axios";
import { fetchDSBatchDetails } from "../../../containers/actions/common/saslAction";

import {
  Visibility,
  Search as SearchIcon,
  ArrowDropDown,
  Refresh,
  ChevronLeft,
  ChevronRight,
  ArrowForwardIos,
} from "@material-ui/icons";

import { PDFViewer } from "../sasl/batch/updateBatch/StructuredInformation/PDFViewer";
import { appUrl } from "../../../../../utils/config";

const initialValuesFromDb = {
  product_name: {
    value: "ENTECAVIR 0.5MG TABLETS",
    confidence: "93.96149444580078"
  },
  product_material_number: {
    value: "3000004524",
    confidence: "94.48526763916016"
  },
  batch_number: {
    value: "CTYBD",
    confidence: "94.80064392089844"
  },
  document_type: "CertificateOfAnalysis",
  folder_name: "Patheon",
  site_name: "Toronto",
  filename: "Patheon-Toronto-CertificateOfAnalysis-45334779-CTYBD.pdf",
  s3_location: "s3://bms-processed-files/Patheon/Toronto/45334779-CTYBD/Patheon-Toronto-CertificateOfAnalysis-45334779-CTYBD.pdf",


};

// styling
const useStyles = makeStyles((theme) => ({
  formLabel: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    minWidth: "150px",
    paddingRight: theme.spacing(2),
    textAlign: "right",
  },
  fixedLabel: {
    width: 180,
    textAlign: "left",
    paddingRight: theme.spacing(2),
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  requiredStar: {
    color: "red",
    marginLeft: 4,
  },
  sectionTitle: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  testTable: {
    '& .MuiTableCell-head': {
      backgroundColor: '#f5f5f5',
      fontWeight: 'bold',
    },
  },
}));

const CertificateOfAnalysis = (props) => {
  const { initialData, onClose, schrockenCustomFileID, digitizedData, batchDetails, poNumber, batchNumber, onDataRefresh } = props;
  const dispatch = useDispatch();
  const [apiData, setApiData] = useState(initialData || {});
  const [originalData, setOriginalData] = useState(initialData || {});
  const [editedData, setEditedData] = useState(initialData || {});
  const [showingOriginal, setShowingOriginal] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [editModalValidationError, setEditModalValidationError] = useState("");
  const [saving, setSaving] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isFetchingHistory, setIsFetchingHistory] = useState(false);
  const [enableAllFieldsEdit, setEnableAllFieldsEdit] = useState(false);
  const [modalData, setModalData] = useState({
    field: "",
    historyData: [],
    hasHistory: false,
  });
  const [editModalData, setEditModalData] = useState({
    fieldKey: "",
    fieldLabel: "",
    currentValue: "",
    originalValue: "",
    confidenceLevel: 0,
    digitalizationCode: "",
    comment: "",
  });
  const [apiDigitalizationCodes, setApiDigitalizationCodes] = useState([]);

  const classes = useStyles();

  // Define form fields for Certificate of Analysis
  const formFields = [
    { key: 'product_name', label: 'Product Name', required: false },
    { key: 'product_material_number', label: 'Product Material Number', required: false },
    { key: 'batch_number', label: 'Batch Number', required: false }
  ];

  // Get Certificate of Analysis data from digitizedExtractedData
  const getCoAFromExtractedData = () => {
    if (batchDetails?.digitizedExtractedData && Array.isArray(batchDetails.digitizedExtractedData)) {
      const coaDoc = batchDetails.digitizedExtractedData.find(
        doc => doc.documentType === 'CertificateOfAnalysis'
      );
      return coaDoc?.CoAJSONOriginal || null;
    }
    return null;
  };

  // Check if we have CoA JSONEdited data
  const hasCoAJSONEditedData = () => {
    const data = batchDetails?.coaJSONEdited;
    return data && typeof data === 'object' && Object.keys(data).length > 0;
  };

  useEffect(() => {
    let dataToUse = initialData || {};
    let originalDataToUse = initialData || {};

    // Prioritize coaJSONEdited if available
    if (hasCoAJSONEditedData()) {
      // console.log("Using coaJSONEdited data");
      dataToUse = batchDetails.coaJSONEdited;
    }

    // Get original unedited data from digitizedExtractedData
    const extractedData = getCoAFromExtractedData();
    if (extractedData) {
      // console.log("Using digitizedExtractedData for original data");
      originalDataToUse = extractedData;
    }

    setApiData(dataToUse);
    setOriginalData(originalDataToUse);
    setEditedData(dataToUse);
  }, [initialData, batchDetails]);

  // Additional useEffect to handle data refresh after successful API calls
  useEffect(() => {
    if (batchDetails?.coaJSONEdited && !showingOriginal) {
      if (hasCoAJSONEditedData()) {
        // console.log("Updating apiData with new coaJSONEdited data");
        setApiData(batchDetails.coaJSONEdited);
        setEditedData(batchDetails.coaJSONEdited);
      }
    }
  }, [batchDetails?.coaJSONEdited, showingOriginal]);

  // Helper function to check if info icon should be shown
  const shouldShowInfoIcon = (fieldKey) => {
    // Don't show info icon in original view
    if (showingOriginal) return false;
    
    const fieldData = batchDetails?.coaJSONEdited?.[fieldKey];
    if (!fieldData || typeof fieldData !== 'object') return false;
    
    return fieldData.needValidation === false && (fieldData.reasonCode || fieldData.comment);
  };

  // Helper function to check if edit button should be shown for validation
  const shouldShowEditForValidation = (fieldKey) => {
    // Don't show edit button in original view
    if (showingOriginal) return false;
    
    // Check edited data first, then fall back to original data
    const currentCoAData = batchDetails?.coaJSONEdited || getCoAFromExtractedData();
    const fieldData = currentCoAData?.[fieldKey];
    if (!fieldData || typeof fieldData !== 'object') return false;
    
    return fieldData.needValidation === true;
  };

  // Helper function to check if editing should be disabled based on needValidation flags
  const isEditingDisabled = () => {
    // Get current CoA data (edited or original)
    const currentCoAData = batchDetails?.coaJSONEdited || getCoAFromExtractedData();
    if (!currentCoAData) return true;
    
    // Check if any field has needValidation: true
    const hasFieldsNeedingValidation = formFields.some(field => {
      const fieldData = currentCoAData[field.key];
      return fieldData && typeof fieldData === 'object' && fieldData.needValidation === true;
    });
    
    // Enable editing if any field has needValidation: true
    return !hasFieldsNeedingValidation;
  };

  // Helper function to get field error props for TextField
  const getFieldErrorProps = (fieldKey) => {
    return {
      error: !!validationErrors[fieldKey],
      helperText: validationErrors[fieldKey]
    };
  };

  // Function to fetch field history from API
  const fetchFieldHistory = async (fieldKey, fieldLabel) => {
    try {
      setIsFetchingHistory(true);
      const requestPayload = {
        schrockenDeliveryScheduleBatchID: batchDetails?.schrockenDeliveryScheduleBatchID,
        dataFieldName: fieldKey,
        documentType: "CertificateOfAnalysis"
      };

      const response = await axios.post(
        `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/workflow/fetchExtractedDataHistory`,
        requestPayload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.status && response.data.fieldDataHistory && response.data.fieldDataHistory.length > 0) {
        // console.log(`Found ${response.data.fieldDataHistory.length} history records for field: ${fieldKey}`);
        // Use all history data from API
        const historyRecords = response.data.fieldDataHistory.map(record => ({
          before: record.beforeValue || 'N/A',
          after: record.afterValue || 'N/A',
          updatedBy: record.lastUpdatedBy || 'System',
          timestamp: record.lastUpdatedDate ? new Date(record.lastUpdatedDate).toLocaleString() : new Date().toLocaleString(),
        }));
        
        setModalData({
          field: fieldLabel,
          historyData: historyRecords,
          hasHistory: true,
        });
      } else {
        setModalData({
          field: fieldLabel,
          historyData: [],
          hasHistory: false,
        });
      }
      setOpenModal(true);
    } catch (error) {
      console.error('Error fetching field history:', error);
      setModalData({
        field: fieldLabel,
        historyData: [],
        hasHistory: false,
      });
      setOpenModal(true);
    } finally {
      setIsFetchingHistory(false);
    }
  };

  // Fetch digitalization codes on component mount
  useEffect(() => {
    const fetchDigitalizationCodes = async () => {
      try {
        const response = await axios.get(
          `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/Admin/fetchDigitalizationCodes`
        );
        // console.log('Digitalization Codes Response:', response.data);
        if (response.data.status && response.data.digitalizationCodesList) {
          // Filter only activated codes and map to the format needed for the dropdown
          const activatedCodes = response.data.digitalizationCodesList
            .filter(code => code.activated && !code.markForDelete)
            .map(code => ({
              code: code.digitalizationCode,
              description: code.digitalizationCodeDescription
            }));
          setApiDigitalizationCodes(activatedCodes);
          // console.log('Processed Digitalization Codes:', activatedCodes);
        }
      } catch (error) {
        console.error('Error fetching digitalization codes:', error);
      }
    };
    fetchDigitalizationCodes();
  }, []);

  const handleOpenEditModal = (fieldKey, fieldLabel) => {
    const currentValue = getValueForField(fieldKey);
    const confidence = getConfidenceForField(fieldKey);
    
    setEditModalData({
      fieldKey,
      fieldLabel,
      currentValue,
      originalValue: currentValue, // Store the original value
      confidenceLevel: confidence,
      digitalizationCode: "",
      comment: "",
    });
    setEditModalValidationError(""); // Clear any previous validation errors
    setOpenEditModal(true);
  };

  const handleSaveFieldEdit = async () => {
    // Validation: if no digitalization code is selected, comment is mandatory
    if (!editModalData.digitalizationCode && !editModalData.comment.trim()) {
      setEditModalValidationError("If reason code is not entered, comment field is mandatory");
      return;
    }

    // Clear validation error if validation passes
    setEditModalValidationError("");

    try {
      setSaving(true);
      
      // Update the field value in apiData
      const newApiData = {
        ...apiData,
        [editModalData.fieldKey]: typeof apiData[editModalData.fieldKey] === 'object' 
          ? { ...apiData[editModalData.fieldKey], value: editModalData.currentValue }
          : editModalData.currentValue,
      };
      setApiData(newApiData);
      setEditedData(newApiData);

      // Get current CoA data and check if field had needValidation: true
      const currentCoAData = batchDetails?.coaJSONEdited || getCoAFromExtractedData();
      
      // Check if the field being edited had needValidation: true
      const wasFieldNeedingValidation = currentCoAData?.[editModalData.fieldKey] && 
        currentCoAData[editModalData.fieldKey].needValidation === true;

      // Update validation counts
      let noOfFieldsToBeReviewed = currentCoAData.noOfFieldsToBeReviewed || 0;
      let noOfFieldsReviewed = currentCoAData.noOfFieldsReviewed || 0;
      
      if (wasFieldNeedingValidation) {
        // Increment reviewed count and decrement to-be-reviewed count
        noOfFieldsReviewed = noOfFieldsReviewed + 1;
        noOfFieldsToBeReviewed = Math.max(0, noOfFieldsToBeReviewed - 1);
        
        // console.log(`Field ${editModalData.fieldKey} with needValidation=true was edited. Updated counters:`, {
        //   noOfFieldsReviewed,
        //   noOfFieldsToBeReviewed
        // });
      }

      // Build updated JSON structure with all fields
      const updatedCoAJSON = {
        ...currentCoAData,
        product_name: {
          value: editModalData.fieldKey === 'product_name' ? editModalData.currentValue : getValueForField('product_name'),
          confidence: getConfidenceForField('product_name'),
          ...(editModalData.fieldKey === 'product_name' && {
            needValidation: false,
            reasonCode: editModalData.digitalizationCode,
            comment: editModalData.comment
          }),
          ...(editModalData.fieldKey !== 'product_name' && currentCoAData.product_name && {
            needValidation: currentCoAData.product_name.needValidation,
            ...(currentCoAData.product_name.reasonCode && { reasonCode: currentCoAData.product_name.reasonCode }),
            ...(currentCoAData.product_name.comment && { comment: currentCoAData.product_name.comment })
          })
        },
        product_material_number: {
          value: editModalData.fieldKey === 'product_material_number' ? editModalData.currentValue : getValueForField('product_material_number'),
          confidence: getConfidenceForField('product_material_number'),
          ...(editModalData.fieldKey === 'product_material_number' && {
            needValidation: false,
            reasonCode: editModalData.digitalizationCode,
            comment: editModalData.comment
          }),
          ...(editModalData.fieldKey !== 'product_material_number' && currentCoAData.product_material_number && {
            needValidation: currentCoAData.product_material_number.needValidation,
            ...(currentCoAData.product_material_number.reasonCode && { reasonCode: currentCoAData.product_material_number.reasonCode }),
            ...(currentCoAData.product_material_number.comment && { comment: currentCoAData.product_material_number.comment })
          })
        },
        batch_number: {
          value: editModalData.fieldKey === 'batch_number' ? editModalData.currentValue : getValueForField('batch_number'),
          confidence: getConfidenceForField('batch_number'),
          ...(editModalData.fieldKey === 'batch_number' && {
            needValidation: false,
            reasonCode: editModalData.digitalizationCode,
            comment: editModalData.comment
          }),
          ...(editModalData.fieldKey !== 'batch_number' && currentCoAData.batch_number && {
            needValidation: currentCoAData.batch_number.needValidation,
            ...(currentCoAData.batch_number.reasonCode && { reasonCode: currentCoAData.batch_number.reasonCode }),
            ...(currentCoAData.batch_number.comment && { comment: currentCoAData.batch_number.comment })
          })
        },
        document_type: currentCoAData.document_type || "CertificateOfAnalysis",
        folder_name: currentCoAData.folder_name || "Patheon",
        site_name: currentCoAData.site_name || "Toronto",
        filename: currentCoAData.filename || digitizedData?.processedFileName,
        s3_location: currentCoAData.s3_location,
        noOfFieldsToBeReviewed: noOfFieldsToBeReviewed,
        noOfFieldsReviewed: noOfFieldsReviewed,
      };

      // console.log("updatedCoAJSON:", updatedCoAJSON);
      // console.log(`After updating JSON - needValidation for ${editModalData.fieldKey}:`, 
      //   updatedCoAJSON[editModalData.fieldKey]?.needValidation);

      // Prepare API request payload for handleDataExtractionEditedData
      const requestPayload = {
        schrockenDeliveryScheduleBatchID: batchDetails?.schrockenDeliveryScheduleBatchID,
        coaJSONEdited: updatedCoAJSON,
        cdmoOrgID: batchDetails?.vendorMosymphonyID,
        file_type: digitizedData?.documentType || "CertificateOfAnalysis",
      };
      // console.log("requestPayload:", requestPayload);

      // Make API call
      const response = await axios.post(
        `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/workflow/handleDataExtractionEditedData`,
        requestPayload,
        {
          headers: {
            "Content-Type": "application/json",
          },
          timeout: 10000,
        }
      );

      if (response.data.status || response.data.success) {
        // console.log('Certificate of Analysis field saved successfully');
        
        // Refresh batch details
        const refreshData = {
          schrockenDeliveryScheduleBatchID: batchDetails?.schrockenDeliveryScheduleBatchID,
        };
        dispatch(fetchDSBatchDetails(refreshData));
        
        setOpenEditModal(false);
      } else {
        console.error('Failed to save:', response.data.message);
        // Close modal even on error to prevent it from getting stuck
        setOpenEditModal(false);
      }
    } catch (error) {
      console.error('Error saving Certificate of Analysis field:', error);
      // Close modal on error to prevent it from getting stuck  
      setOpenEditModal(false);
    } finally {
      setSaving(false);
    }
  };

  // Validation functions
  const validateField = (fieldKey, value) => {
    const field = formFields.find(f => f.key === fieldKey);
    if (!field) return null;

    if (field.required && (!value || value.trim() === '')) {
      return `${field.label} is required`;
    }

    // Specific validations based on field type
    switch (fieldKey) {
      case 'product_name':
        if (value && value.length < 3) {
          return "Product Name should be more detailed";
        }
        break;
      case 'product_material_number':
        if (value && !/^\d+$/.test(value)) {
          return "Must be numeric";
        }
        break;
      case 'batch_number':
        // Allow alphanumeric for batch numbers
        if (value && !/^[a-zA-Z0-9]*$/.test(value)) {
          return "Must contain only alphanumeric characters";
        }
        break;
      default:
        break;
    }

    return null;
  };

  const validateAllFields = () => {
    const errors = {};
    formFields.forEach(field => {
      const error = validateField(field.key, getValueForField(field.key));
      if (error) {
        errors[field.key] = error;
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const getConfidenceForField = (fieldKey) => {
    if (showingOriginal) {
      // When showing original, get confidence from digitizedExtractedData
      const originalData = getCoAFromExtractedData();
      const fieldData = originalData?.[fieldKey];
      if (fieldData && typeof fieldData === 'object' && fieldData.confidence != null) {
        return parseFloat(fieldData.confidence);
      }
      // If field exists but has no value, return 0
      if (fieldData && typeof fieldData === 'object' && (!fieldData.value || fieldData.value.trim() === '')) {
        return 0;
      }
      // If field doesn't exist in response, return 0
      return 0;
    }
    
    // Otherwise use the current apiData (which is edited data)
    const fieldData = apiData[fieldKey];
    if (fieldData && typeof fieldData === 'object' && fieldData.confidence != null) {
      return parseFloat(fieldData.confidence);
    }
    
    // If field exists but has no value, return 0
    if (fieldData && typeof fieldData === 'object' && (!fieldData.value || fieldData.value.trim() === '')) {
      return 0;
    }
    
    // For fields that don't have confidence data or don't exist in the API response, return 0
    return 0;
  };

  const getValueForField = (fieldKey) => {
    const fieldData = apiData[fieldKey];
    if (fieldData && typeof fieldData === 'object' && fieldData.value !== undefined) {
      return fieldData.value || '';
    }
    return fieldData || '';
  };

  const getLevelColor = (level) => {
    if (level === null || level === undefined) return "#cccccc"; // Gray for N/A
    
    // Get dynamic confidence levels from batch details
    const confidenceLevels = batchDetails?.confidenceLevels;
    const lowConfidenceLimit = confidenceLevels?.lowConfidenceLimit || 80;
    const highConfidenceLimit = confidenceLevels?.highConfidenceLimit || 90;
    
    if (level < lowConfidenceLimit) return "red";
    if (level <= highConfidenceLimit) return "orange";
    return "green";
  };

  const getConfidenceDisplay = (fieldKey) => {
    const confidence = getConfidenceForField(fieldKey);
    return confidence !== null ? Math.round(confidence) : "N/A";
  };

  const handleInputChange = (fieldKey, value) => {
    const newApiData = {
      ...apiData,
      [fieldKey]: typeof apiData[fieldKey] === 'object' 
        ? { ...apiData[fieldKey], value: value }
        : value,
    };
    setApiData(newApiData);
    if (!showingOriginal) {
      setEditedData(newApiData);
    }

    // Validate the field and update errors
    const error = validateField(fieldKey, value);
    setValidationErrors(prev => ({
      ...prev,
      [fieldKey]: error
    }));
  };

  const handleSave = async () => {
    if (validateAllFields()) {
      try {
        setSaving(true);
        
        // Get current CoA data (edited or original)
        const currentCoAData = batchDetails?.coaJSONEdited || getCoAFromExtractedData() || {};
        
        // Update JSON with current form values
        const updatedCoAJSON = {
          ...currentCoAData,
          product_name: {
            value: getValueForField('product_name'),
            confidence: getConfidenceForField('product_name'),
            ...(currentCoAData.product_name && {
              needValidation: currentCoAData.product_name.needValidation,
              ...(currentCoAData.product_name.reasonCode && { reasonCode: currentCoAData.product_name.reasonCode }),
              ...(currentCoAData.product_name.comment && { comment: currentCoAData.product_name.comment })
            })
          },
          product_material_number: {
            value: getValueForField('product_material_number'),
            confidence: getConfidenceForField('product_material_number'),
            ...(currentCoAData.product_material_number && {
              needValidation: currentCoAData.product_material_number.needValidation,
              ...(currentCoAData.product_material_number.reasonCode && { reasonCode: currentCoAData.product_material_number.reasonCode }),
              ...(currentCoAData.product_material_number.comment && { comment: currentCoAData.product_material_number.comment })
            })
          },
          batch_number: {
            value: getValueForField('batch_number'),
            confidence: getConfidenceForField('batch_number'),
            ...(currentCoAData.batch_number && {
              needValidation: currentCoAData.batch_number.needValidation,
              ...(currentCoAData.batch_number.reasonCode && { reasonCode: currentCoAData.batch_number.reasonCode }),
              ...(currentCoAData.batch_number.comment && { comment: currentCoAData.batch_number.comment })
            })
          },
          document_type: currentCoAData.document_type || "CertificateOfAnalysis",
          folder_name: currentCoAData.folder_name || "Patheon",
          site_name: currentCoAData.site_name || "Toronto",
          filename: currentCoAData.filename || digitizedData?.processedFileName,
          s3_location: currentCoAData.s3_location,
          noOfFieldsToBeReviewed: currentCoAData.noOfFieldsToBeReviewed || 0,
          noOfFieldsReviewed: currentCoAData.noOfFieldsReviewed || 0
        };

        // Prepare API request payload for handleDataExtractionEditedData
        const requestPayload = {
          schrockenDeliveryScheduleID: batchDetails?.schrockenDeliveryScheduleID,
        schrockenDeliveryScheduleBatchID: batchDetails?.schrockenDeliveryScheduleBatchID,
          coaJSONEdited: updatedCoAJSON,
          poNumber: poNumber,
        batchNumber: batchNumber,
        cdmoOrgID: batchDetails?.vendorMosymphonyID,
        file_type: "CertificateOfAnalysis",
        };

        // Make API call
        const response = await axios.post(
          `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/workflow/handleDataExtractionEditedData`,
          requestPayload,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (response.data.status) {
          // Save changes locally
          setEditedData(apiData);
          setShowingOriginal(false);
          
          // Refresh batch details
          const refreshData = {
            schrockenDeliveryScheduleBatchID: batchDetails?.schrockenDeliveryScheduleBatchID,
          };
          dispatch(fetchDSBatchDetails(refreshData));
          
          // console.log('Certificate of Analysis data saved successfully');
        } else {
          console.error('Failed to save:', response.data.message);
        }
      } catch (error) {
        console.error('Error saving Certificate of Analysis data:', error);
      } finally {
        setSaving(false);
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'pass':
        return 'green';
      case 'fail':
        return 'red';
      case 'pending':
        return 'orange';
      default:
        return 'gray';
    }
  };

  return (
    <>
      {/* Loading overlay when fetching field history */}
      {isFetchingHistory && (
        <Box
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <Box 
            style={{
              backgroundColor: 'white',
              padding: 24,
              borderRadius: 8,
              textAlign: 'center',
              minWidth: 200,
            }}
          >
            <CircularProgress size={40} style={{ marginBottom: 16 }} />
            <Typography variant="h6">Loading field history...</Typography>
          </Box>
        </Box>
      )}

      {/* Loading overlay when refreshing data */}
      {isRefreshing && (
        <Box
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <Box 
            style={{
              backgroundColor: 'white',
              padding: 24,
              borderRadius: 8,
              textAlign: 'center',
              minWidth: 200,
            }}
          >
            <CircularProgress size={40} style={{ marginBottom: 16 }} />
            <Typography variant="h6">Refreshing data...</Typography>
          </Box>
        </Box>
      )}
      
      <Grid container direction="column" spacing={2}>
        {/* Actions*/}
        <Grid item container alignItems="center" spacing={2}>
          <Grid item xs={6}>
            <Grid item style={{display: "flex", justifyContent: "space-between", alignItems: "center"}} spacing={2}>
              <Typography
                variant="h6"
                style={{
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  marginRight: "8px",
                }}
              >
                Certificate of Analysis Data
              </Typography>
              <Box display="flex" gap={1} spacing={2}>
                <Button
                  color="secondary"
                  variant="outlined"
                  onClick={() => {
                    if (showingOriginal) {
                      setApiData(editedData);
                      setShowingOriginal(false);
                    } else {
                      setApiData(originalData);
                      setShowingOriginal(true);
                    }
                  }}
                  style={{  minWidth: 20, textAlign: "center", size: "20px" }}
                >
                  {showingOriginal ? "Show Edited Version" : "Show Unedited Version"}
                </Button>
              </Box>
            </Grid>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="h6">PDF Preview</Typography>
          </Grid>
        </Grid>

        

        <Grid item container spacing={2}>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={2}
              style={{
                height: "calc(100vh - 200px)",
                padding: 16,
                overflowY: "auto",
              }}
            >
              {/* Dynamic Field Rendering */}
              {formFields.map((field) => (
                <Grid
                  container
                  key={field.key}
                  alignItems="center"
                  spacing={2}
                  className={classes.formField}
                >
                  <Grid item xs={4}>
                    <Box display="flex" alignItems="center">
                      <Typography style={{ paddingRight: 16, fontWeight: 500 }}>
                        {field.label}
                        {field.required && (
                          <span className={classes.requiredStar}>*</span>
                        )}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={8}>
                    <Box display="flex" alignItems={field.key === 'materialDescription' ? "flex-start" : "center"} gap={1}>
                      <Box style={{ width: showingOriginal ? "calc(100% - 60px)" : "calc(100% - 40px)" }}>
                        <TextField
                          variant="outlined"
                          fullWidth
                          size="small"
                          multiline={field.key === 'materialDescription'}
                          rows={field.key === 'materialDescription' ? 2 : 1}
                          value={getValueForField(field.key)}
                          placeholder={`Enter ${field.label}`}
                          InputProps={{
                            readOnly: true,
                            endAdornment: (
                              <InputAdornment position="end">
                                <Box display="flex" alignItems="center" pl={1}>
                                  <Box style={{ width: 1, height: field.key === 'materialDescription' ? 50 : 39, backgroundColor: "#ccc", marginRight: 3 }} />
                                  {shouldShowInfoIcon(field.key) && !showingOriginal ? (
                                    <Box
                                      onClick={() => fetchFieldHistory(field.key, field.label)}
                                      style={{
                                        backgroundColor: "#777",
                                        color: "white",
                                        borderRadius: 4,
                                        padding: "2px 8px",
                                        fontSize: "0.75rem",
                                        fontWeight: 600,
                                        minWidth: 24,
                                        textAlign: "center",
                                        cursor: "pointer",
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      <Info fontSize="small" />
                                    </Box>
                                  ) : (
                                    <Box style={{
                                      backgroundColor: getLevelColor(getConfidenceForField(field.key)),
                                      color: "white",
                                      borderRadius: 4,
                                      padding: "2px 8px",
                                      fontSize: "0.75rem",
                                      fontWeight: 600,
                                      minWidth: 24,
                                      textAlign: "center",
                                    }}>
                                      {getConfidenceDisplay(field.key)}
                                    </Box>
                                  )}
                                </Box>
                              </InputAdornment>
                            ),
                            ...getFieldErrorProps(field.key)
                          }}
                        />
                      </Box>
                      <Box style={{ width: 32, height: 32, display: "flex", alignItems: field.key === 'materialDescription' ? "flex-start" : "center", justifyContent: "center", ...(field.key === 'materialDescription' && { paddingTop: 4 }) }}>
                        {(enableAllFieldsEdit || shouldShowInfoIcon(field.key) || shouldShowEditForValidation(field.key)) && !showingOriginal && !isEditingDisabled() ? (
                          <Box
                            onClick={() => handleOpenEditModal(field.key, field.label)}
                            style={{
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Edit fontSize="small" style={{ color: "#EC6535" }} />
                          </Box>
                        ) : null}
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              ))}

              <Box display="flex" alignItems="center" gap={2} mb={2} mt={3}>
                <Typography variant="body2" color="textSecondary">
                  Confidence Levels Legend:{" "}
                </Typography>
                <Grid item style={{ width: "28px" }} />
                {(() => {
                  // Get dynamic confidence levels from batch details
                  const confidenceLevels = batchDetails?.confidenceLevels;
                  const lowConfidenceLimit = confidenceLevels?.lowConfidenceLimit || 80;
                  const highConfidenceLimit = confidenceLevels?.highConfidenceLimit || 90;

                  
                  return (
                    <>
                      {/* Green Box */}
                      <Box
                        style={{
                          backgroundColor: "green",
                          color: "white",
                          borderRadius: 4,
                          padding: "4px 12px",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          textAlign: "center",
                          minWidth: 30,
                        }}
                      >
                        &gt; {highConfidenceLimit}
                      </Box>
                      {/* Orange Box */}
                      <Box
                        style={{
                          backgroundColor: "orange",
                          color: "white",
                          borderRadius: 4,
                          padding: "4px 12px",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          textAlign: "center",
                          minWidth: 50,
                          marginLeft: 16,
                        }}
                      >
                        {confidenceLevels?.mediumConfidenceLimitMinValue} - {confidenceLevels?.mediumConfidenceLimitMaxValue}
                      </Box>
                      {/* Red Box */}
                      <Box
                        style={{
                          backgroundColor: "red",
                          color: "white",
                          borderRadius: 4,
                          padding: "4px 12px",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          textAlign: "center",
                          minWidth: 30,
                          marginLeft: 16,
                        }}
                      >
                        &lt; {lowConfidenceLimit}
                      </Box>
                    </>
                  );
                })()}
              </Box>
            </Paper>
          </Grid>

          {/* Right side: PDF view */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={2}
              style={{
                height: "calc(100vh - 200px)",
                padding: 16,
                overflowY: "auto",
              }}
            >
              {schrockenCustomFileID ? (
                <PDFViewer schrockenCustomFileID={schrockenCustomFileID} />
              ) : (
                <Box
                  style={{
                    height: 500,
                    backgroundColor: "#f5f5f5",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1px dashed #ccc",
                  }}
                >
                  <Typography>PDF not available</Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Edit All Fields Option */}
        <Grid item xs={12}>
          <Box 
            style={{
              border: "1px solid #ddd",
              padding: "12px",
              borderRadius: "4px",
              backgroundColor: "#f9f9f9",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={enableAllFieldsEdit}
                  onChange={(e) => setEnableAllFieldsEdit(e.target.checked)}
                  color="primary"
                  disabled={isEditingDisabled()}
                />
              }
              label="Apply edit feature to all the fields"
            />
            <Typography 
              variant="body2" 
              style={{ 
                fontStyle: "italic", 
                color: "#666", 
                marginTop: "4px" 
              }}
            >
              Note - This will enable editing of the high confidence level fields, highlighted in green colors.
            </Typography>
          </Box>
        </Grid>
      </Grid>

      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "#fff",
            padding: 24,
            boxShadow: 24,
            borderRadius: 8,
            minWidth: 600,
            maxHeight: "80vh",
            overflowY: "auto",
          }}
        >
          <Typography variant="h6" gutterBottom>
            {modalData.field} - Field Data History
          </Typography>

          {isFetchingHistory ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
              <Typography variant="body2" style={{ marginLeft: 16 }}>
                Loading field history...
              </Typography>
            </Box>
          ) : modalData.hasHistory ? (
            <Table>
              <TableHead style={{ backgroundColor: "#f5f5f5" }}>
                <TableRow>
                  <TableCell><b>Timestamp</b></TableCell>
                  <TableCell><b>Before Value</b></TableCell>
                  <TableCell><b>After Value</b></TableCell>
                  <TableCell><b>Updated By</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {modalData.historyData.map((record, index) => (
                  <TableRow key={index}>
                    <TableCell>{record.timestamp}</TableCell>
                    <TableCell>{record.before}</TableCell>
                    <TableCell>{record.after}</TableCell>
                    <TableCell>{record.updatedBy}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Box textAlign="center" py={4}>
              <Typography variant="body1" color="textSecondary">
                No history data available for {modalData.field}
              </Typography>
            </Box>
          )}

          <Box display="flex" justifyContent="center" mt={3}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setOpenModal(false)}
            >
              OK
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Edit Field Modal */}
      <Modal open={openEditModal} onClose={() => setOpenEditModal(false)}>
        <Box
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "#fff",
            padding: 24,
            boxShadow: 24,
            borderRadius: 8,
            minWidth: 600,
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Edit {editModalData.fieldLabel} for PO {digitizedData?.poNumber || ''}
            </Typography>
            <Button onClick={() => setOpenEditModal(false)} style={{ minWidth: 'auto' }}>
              ✕
            </Button>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={6}>
              <Typography variant="body2" gutterBottom style={{ fontWeight: 'bold' }}>
                {editModalData.fieldLabel}
              </Typography>
              <Box display="flex" alignItems="center">
                <TextField
                  variant="outlined"
                  fullWidth
                  size="small"
                  value={editModalData.currentValue}
                  onChange={(e) => setEditModalData(prev => ({ ...prev, currentValue: e.target.value }))}
                  onBlur={(e) => {
                    const { value } = e.target;
                    // Trim leading and trailing spaces for string values
                    if (typeof value === "string" && value.trim() !== value) {
                      setEditModalData(prev => ({ ...prev, currentValue: value.trim() }));
                    }
                  }}
                />
                {((editModalData.confidenceLevel !== null && editModalData.confidenceLevel > 90) || shouldShowInfoIcon(editModalData.fieldKey)) ? null : (
                  <Box
                    onClick={() => {
                      if (editModalData.currentValue === editModalData.originalValue) {
                        setEditModalData(prev => ({ ...prev, digitalizationCode: "DG001" }))
                      }
                    }}
                    style={{
                      backgroundColor: editModalData.currentValue === editModalData.originalValue ? "green" : "#cccccc",
                      color: "white",
                      borderRadius: 4,
                      padding: "2px 8px",
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      minWidth: 24,
                      textAlign: "center",
                      cursor: editModalData.currentValue === editModalData.originalValue ? "pointer" : "not-allowed",
                      opacity: editModalData.currentValue === editModalData.originalValue ? 1 : 0.5,
                      marginLeft: 8,
                    }}
                  >
                    ✓
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" gutterBottom style={{ fontWeight: 'bold' }}>
                Confidence Level (%)
              </Typography>
              <Box
                
              >
                <Typography variant="body2">
                  {editModalData.confidenceLevel !== null ? Math.round(editModalData.confidenceLevel) : "N/A"}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" gutterBottom style={{ fontWeight: 'bold' }}>
                Digitalization Code / Description
              </Typography>
              <FormControl fullWidth variant="outlined" size="small">
                <Select
                  value={editModalData.digitalizationCode}
                  onChange={(e) => {
                    setEditModalData(prev => ({ ...prev, digitalizationCode: e.target.value }));
                    setEditModalValidationError(""); // Clear validation error when user selects a code
                  }}
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>Select a digitalization code</em>
                  </MenuItem>
                  {apiDigitalizationCodes.map((code) => (
                    <MenuItem key={code.code} value={code.code}>
                      {code.code} / {code.description}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" gutterBottom style={{ fontWeight: 'bold' }}>
                Comment
              </Typography>
              <TextField
                variant="outlined"
                fullWidth
                size="small"
                placeholder="Enter text here"
                value={editModalData.comment}
                onChange={(e) => {
                  if(e.target.value.length < 256){
                  setEditModalData(prev => ({ ...prev, comment: e.target.value }));
                  }
                  setEditModalValidationError(""); // Clear validation error when user types in comment
                }}
                onBlur={(e) => {
                  const { value } = e.target;
                  // Trim leading and trailing spaces for string values
                  if (typeof value === "string" && value.trim() !== value) {
                    setEditModalData(prev => ({ ...prev, comment: value.trim() }));
                  }
                }}
                multiline
                rows={3}
              />
              {editModalValidationError && (
                <Typography 
                  variant="body2" 
                  color="error" 
                  style={{ 
                    marginTop: 8, 
                    padding: "8px 12px",
                    backgroundColor: "#ffebee",
                    border: "1px solid #f44336",
                    borderRadius: 4
                  }}
                >
                  {editModalValidationError}
                </Typography>
              )}
              <Typography variant="caption" color="textSecondary" style={{ marginTop: 4 }}>
                Note - If reason code is not entered, comment field is mandatory
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" justifyContent="center">
                <Button
                  variant="contained"
                  style={{borderRadius: 30}}
                  color="primary"
                  onClick={handleSaveFieldEdit}
                  disabled={saving}
                  endIcon={saving ? <CircularProgress size={20} /> : <ArrowForwardIos style={{ fontSize: 15 }} />}
                >
                  {saving ? "Saving..." : "Save changes"}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Modal>
    </>
  );
};

export default CertificateOfAnalysis;